// Round-based country cycling.
//
// We cycle through every country in the pool once per "round" — shuffled,
// no repeats — before starting a new round with a fresh shuffle. This is
// fairer than weighted random with replacement, which can pile repeats on
// a handful of countries while ignoring others entirely.
//
// Wrong answers re-enter the current round's queue a couple of positions
// back, so the user sees the country again soon (but not back-to-back) for
// extra exposure and better retention. Each country can only be re-queued
// once per round — otherwise a persistently-wrong country would dominate
// the queue and the round would never end, starving the rest of the pool
// of fair exposure.
//
// Pure — the caller owns the RoundState and the RNG, so tests can be
// deterministic.

export interface RoundState {
  pool: string[];
  remaining: string[];
  roundNumber: number;
  // ISOs already re-queued this round. Cleared when a new round starts.
  // Tracked as a sorted array (not a Set) so RoundState stays a plain
  // object that survives structured clone / JSON round-trips if a caller
  // ever decides to persist it.
  requeued: string[];
}

export interface DrawResult {
  iso: string | null;
  state: RoundState;
}

// Avoid back-to-back repetition when a missed country is re-queued: keep at
// least this many other countries between the miss and its next showing,
// unless the queue is too small.
const MIN_RETRY_GAP = 2;

const shuffled = (isos: string[], rng: () => number): string[] => {
  const out = isos.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = out[i]!;
    out[i] = out[j]!;
    out[j] = tmp;
  }
  return out;
};

export const startRound = (
  pool: string[],
  rng: () => number,
): RoundState => ({
  pool: pool.slice(),
  remaining: shuffled(pool, rng),
  roundNumber: 1,
  requeued: [],
});

export const drawNext = (
  state: RoundState,
  rng: () => number,
): DrawResult => {
  if (state.pool.length === 0) {
    return { iso: null, state };
  }
  let remaining = state.remaining;
  let roundNumber = state.roundNumber;
  let requeued = state.requeued;
  if (remaining.length === 0) {
    remaining = shuffled(state.pool, rng);
    roundNumber += 1;
    requeued = [];
  }
  const iso = remaining[0]!;
  return {
    iso,
    state: { ...state, remaining: remaining.slice(1), roundNumber, requeued },
  };
};

export const returnMissed = (
  state: RoundState,
  iso: string,
  rng: () => number,
): RoundState => {
  // Cap at one re-queue per country per round. Without this, a country the
  // user keeps getting wrong piles back into the queue forever, drowning
  // out the rest of the pool and blocking the round from ever ending.
  if (state.requeued.includes(iso)) return state;
  const remaining = state.remaining.slice();
  const minIdx = Math.min(MIN_RETRY_GAP, remaining.length);
  const maxIdx = remaining.length;
  const idx = minIdx + Math.floor(rng() * (maxIdx - minIdx + 1));
  remaining.splice(idx, 0, iso);
  return { ...state, remaining, requeued: [...state.requeued, iso] };
};
